/**
 * AddRequirementModal Component
 * 
 * Modal form for adding new requirements
 */

import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, Button } from 'antd';
import { RequirementState } from '../utils/formTransformers';
import { requirementValidationRules } from '../utils/validators';

const { TextArea } = Input;

interface AddRequirementModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (requirement: RequirementState) => void;
  editingRequirement?: RequirementState | null;
  mustMatchCount: number;
}

const AddRequirementModal: React.FC<AddRequirementModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  editingRequirement,
  mustMatchCount,
}) => {
  const [form] = Form.useForm();
  const isEditing = !!editingRequirement;
  
  // Check if must-match switch should be disabled
  // Disable if editing a must-match requirement and it's the only one
  const isMustMatchDisabled = isEditing && editingRequirement?.mustMatch && mustMatchCount === 1;

  // Reset form when modal opens/closes or editing requirement changes
  useEffect(() => {
    if (visible) {
      if (editingRequirement) {
        form.setFieldsValue({
          description: editingRequirement.description,
          mustMatch: editingRequirement.mustMatch,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          mustMatch: true, // Default to must-match
        });
      }
    } else {
      form.resetFields();
    }
  }, [visible, editingRequirement, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const requirement: RequirementState = {
        id: editingRequirement?.id || `temp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        description: values.description.trim(),
        mustMatch: values.mustMatch ?? true, // Default to true for new requirements
        order: editingRequirement?.order || 0,
      };

      onSubmit(requirement);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={isEditing ? 'Edit Requirement' : 'Add Requirement'}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {isEditing ? 'Save Changes' : 'Add Requirement'}
        </Button>,
      ]}
      width={600}
      maskClosable={false}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="description"
          label="Requirement Description"
          rules={requirementValidationRules}
        >
          <TextArea
            placeholder="e.g., 5+ years of experience in React and Node.js"
            autoSize={{ minRows: 4, maxRows: 8 }}
          />
        </Form.Item>

        <Form.Item
          name="mustMatch"
          label={
            <div>
              <span>Must Match</span>
              <div style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)', fontWeight: 'normal', marginTop: '4px' }}>
                {isMustMatchDisabled 
                  ? "At least one 'Must Match' requirement is required. You cannot disable this requirement as it is the only must-match requirement remaining."
                  : "Requirements marked as 'Must Match' are critical and candidates must meet these criteria. At least one must-match requirement is required."
                }
              </div>
            </div>
          }
          valuePropName="checked"
        >
          <Switch 
            checkedChildren="Must Match" 
            unCheckedChildren="Optional" 
            disabled={isMustMatchDisabled}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddRequirementModal;

